import { IframeMessageType, MessageListener, MessagesKeys } from './model';

export type IframeMessageBrokerInstanceType = ReturnType<
  (typeof IframeMessageBroker)['getInstance']
>;
const IframeMessageBroker = (function () {
  let instance: {
    addListener: (messageType: MessagesKeys, callback: MessageListener) => void;
    removeListener: (messageType: MessagesKeys, callback: MessageListener) => void;
    sendMessage: <T extends IframeMessageType>(
      messageType: T['messageName'],
      messagePayload: T['payload'],
      targetIframe?: HTMLIFrameElement | Window,
      domain?: string
    ) => void;
    reset: () => void;
  };

  function createInstance() {
    let listeners: { [messageType in MessagesKeys]?: MessageListener[] } = {};

    const addListener = (messageType: MessagesKeys, callback: MessageListener): void => {
      if (!listeners[messageType]) {
        listeners[messageType] = [];
      }
      listeners[messageType].push(callback);
    };

    const removeListener = (messageType: MessagesKeys, callback: MessageListener): void => {
      if (!listeners[messageType]) return;
      listeners[messageType] = listeners[messageType].filter(listener => listener !== callback);
    };

    const sendMessage = <T extends IframeMessageType>(
      messageType: T['messageName'],
      messagePayload: T['payload'],
      targetIframe?: HTMLIFrameElement | Window,
      domain: string = '*'
    ): void => {
      const target = targetIframe
        ? ((targetIframe as HTMLIFrameElement)?.contentWindow ?? window.parent)
        : window.parent;
      const message = {
        messageName: messageType,
        payload: messagePayload
      };
      target.postMessage(message, domain);
    };

    const handleMessage = (event: MessageEvent): void => {
      const { messageName, payload } = event.data as IframeMessageType;
      if (listeners[messageName]) {
        listeners[messageName].forEach(callback => callback(payload));
      }
    };

    const reset = () => {
      listeners = {};
    };

    window.addEventListener('message', handleMessage, false);

    return {
      addListener,
      removeListener,
      sendMessage,
      reset
    };
  }

  return {
    getInstance: () => {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }
  };
})();

export default IframeMessageBroker;
